import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { isEqual } from "lodash";

import { Select } from "components/form_components/Inputs";

export type MultiSelectOption = { label: string; value: string };

type Props = {
  label: string;
  placeholder?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
};

type FormShape = { value: string[] };

/**
 * A labelled multi-select filter field matching the buyer selects (form_components
 * `Inputs.Select`) so the contacts sidebar reads as one family. `Inputs.Select` is a
 * react-hook-form field, so we host a one-field local form and bridge it to the
 * sidebar's plain value/onChange state — the same pattern as CognismContactsBuyersFilter.
 */
function LabelledMultiSelectFilter({ label, placeholder, options, value, onChange }: Props) {
  const { control, watch, reset, getValues } = useForm<FormShape>({ defaultValues: { value } });

  // Keep the latest onChange in a ref so the watch subscription isn't torn down each render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // The last value reconciled in either direction — guards both effects from looping.
  const lastSynced = useRef<string[]>(value);

  // Form edits → parent.
  useEffect(() => {
    const subscription = watch(() => {
      const next = getValues().value ?? [];
      if (!isEqual(next, lastSynced.current)) {
        lastSynced.current = next;
        onChangeRef.current(next);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  // External replace (e.g. "Clear all") → form; skipped for our own edits.
  useEffect(() => {
    if (!isEqual(value, lastSynced.current)) {
      lastSynced.current = value;
      reset({ value });
    }
  }, [value, reset]);

  return (
    <Select<FormShape>
      control={control}
      name="value"
      label={label}
      placeholder={placeholder}
      options={options}
      mode="multiple"
      allowClear
    />
  );
}

export default LabelledMultiSelectFilter;
